import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError } from '../services/api'
import { useStoreOwner } from '../store/useStoreOwner'
import { useStaffAuth } from '../store/useStaffAuth'

/**
 * 許可を確かめ済みの「アカウント | 店」の組。画面を移るたびに往復しないためのもの。
 * アカウントが変われば鍵が変わるので持ち越さない。**ここは表示の門で、守りの本体は API**
 * (店舗の API はすべて `_role_for_store` で弾く) — 途中で権限を外されたときは API が 403 を返す。
 */
const allowedKeys = new Set<string>()

type CheckState = 'allowed' | 'denied' | 'error'

/**
 * #1910 `/store/.../:storeId` の画面を、その店を扱ってよいアカウントにだけ見せる門。
 *
 * これまで店舗管理の画面は「ログイン済みの承認済みオーナー」しか確かめておらず、URL を知っていれば
 * 関係の無いアカウントでも中身つきで開けた (店舗情報はお客様向けの公開 GET で読むのでフォームが
 * 埋まる)。ここで `GET /stores/{id}/access` (店舗の API と同じ判定) を叩き、**許可が確かめられる
 * までは中身を描かない**。403 / 404 は「権限がありません」、通信断などで確かめられないときも
 * 中身は出さずにやり直しを出す (fail-closed)。
 *
 * スタッフのセッションがあるときはスタッフとして確かめる (apiFetch が店舗の画面ではスタッフの
 * トークンを優先するのと揃える)。
 */
export default function RequireStoreAccess({ children }: { children: ReactNode }) {
  const { storeId } = useParams<{ storeId?: string }>()
  const { t } = useI18n()
  const navigate = useNavigate()
  const ownerUid = useStoreOwner((s) => s.firebaseUser?.uid)
  const staffSession = useStaffAuth((s) => s.session)
  const identity = staffSession
    ? `staff:${staffSession.storeId}:${staffSession.memberId}`
    : ownerUid ? `owner:${ownerUid}` : ''
  const key = storeId && identity ? `${identity}|${storeId}` : ''

  const [check, setCheck] = useState<{ key: string; state: CheckState } | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!key || !storeId || allowedKeys.has(key)) return
    let cancelled = false
    api.getStoreAccess(storeId)
      .then(() => {
        allowedKeys.add(key)
        if (!cancelled) setCheck({ key, state: 'allowed' })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        // 店が無い (404) も「権限がありません」と同じに見せる (他人の店があるかどうかを教えない)
        const denied = e instanceof ApiError && (e.status === 403 || e.status === 404)
        if (!denied) console.error('[RequireStoreAccess] access check failed:', e)
        setCheck({ key, state: denied ? 'denied' : 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [key, storeId, attempt])

  // 店を URL に持たない画面 (ダッシュボード一覧・アカウント設定など) はこの門の対象外
  if (!storeId) return <>{children}</>

  const state: CheckState | 'checking' = allowedKeys.has(key)
    ? 'allowed'
    : check?.key === key ? check.state : 'checking'

  if (state === 'allowed') return <>{children}</>

  if (state === 'checking') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="store-access-checking">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center" data-testid="store-access-error">
        <p className="max-w-sm font-body text-sm leading-relaxed text-text-secondary">{t('storeNav.accessCheckFailed')}</p>
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="mt-5 h-11 rounded-xl border border-navy/30 bg-white px-6 font-body text-sm font-bold text-navy tap-effect"
        >
          {t('storeNav.accessCheckRetry')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center" data-testid="store-access-denied">
      <ShieldAlert size={40} className="text-yokoso-red" aria-hidden />
      <h1 className="mt-4 font-display text-lg font-bold text-text-primary">{t('storeNav.accessDeniedTitle')}</h1>
      <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-text-secondary">{t('storeNav.accessDeniedBody')}</p>
      <button
        type="button"
        onClick={() => navigate('/store/dashboard', { replace: true })}
        className="mt-6 h-11 rounded-xl bg-navy px-6 font-body text-sm font-bold text-white tap-effect"
      >
        {t('storeNav.accessDeniedBack')}
      </button>
    </div>
  )
}

/**
 * #1910 `/store/settings` `/store/report` の店 ID を持たない入口。
 *
 * 以前は画面側で `?store=` が無ければ `store_001` を開いていたため、どのオーナーでも既定の店の
 * 中身が見えた。`?store=` があれば `/:storeId` 付きの URL へ移して上の門を通し
 * (他の query はそのまま)、無ければ自分の店を選べるダッシュボードへ移す。
 */
export function StoreQueryRedirect({ base }: { base: string }) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const storeId = params.get('store')
  if (!storeId) return <Navigate to="/store/dashboard" replace />
  params.delete('store')
  const rest = params.toString()
  return <Navigate to={`${base}/${encodeURIComponent(storeId)}${rest ? `?${rest}` : ''}`} replace />
}
